import { useState } from 'react';
import { Button } from '~/components/ui/button';
import { familyLabel } from '~/utils/familyLabel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { useMarkFamilyEnrolled } from './useMarkFamilyEnrolled';

interface MarkEnrolledDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  familyEnrollmentId: string;
  familyName: string;
  yearName: string;
  /** Required steps the family can see that are not done, waived or not needed. */
  openSteps: number;
  /** The children Coming, who become Enrolled with the family. */
  comingChildren: string[];
  onSuccess?: () => void;
}

// "Anna", "Anna and Ben", "Anna, Ben and Cara".
const andList = (names: string[]) =>
  names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

// The office marks a family's year enrolled, whatever is still open. The
// server's answer decides: a refusal stays on screen and the dialog stays open.
export function MarkEnrolledDialog({
  open,
  onOpenChange,
  familyEnrollmentId,
  familyName,
  yearName,
  openSteps,
  comingChildren,
  onSuccess,
}: MarkEnrolledDialogProps) {
  const [errors, setErrors] = useState<string[]>([]);
  const [markEnrolled, { loading }] = useMarkFamilyEnrolled();

  const handleOpenChange = (next: boolean) => {
    if (!next) setErrors([]);
    onOpenChange(next);
  };

  const handleConfirm = async () => {
    try {
      const result = await markEnrolled({ variables: { input: { familyEnrollmentId } } });
      const payload = result.data?.markFamilyEnrolled;
      if (payload?.errors && payload.errors.length > 0) {
        setErrors(payload.errors);
        return;
      }
      if (!payload?.familyEnrollment) {
        setErrors(['The family was not marked enrolled. Please try again.']);
        return;
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'The family was not marked enrolled. Please try again.']);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{`Mark the ${familyLabel(familyName)} enrolled for ${yearName}?`}</DialogTitle>
          <DialogDescription className="sr-only">Marks this family's year enrolled.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {comingChildren.length > 0 && <p>{`${andList(comingChildren)} will become Enrolled.`}</p>}
          {openSteps > 0 && (
            <p>{openSteps === 1 ? "1 step is still open. It'll stay as a thing to do." : `${openSteps} steps are still open. They'll stay as things to do.`}</p>
          )}
        </div>
        {errors.length > 0 && (
          <div role="alert" className="space-y-1 text-sm text-destructive">
            {errors.map((message) => (
              <p key={message}>{message}</p>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading}>
            {loading ? 'Marking...' : 'Mark enrolled'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
